import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Writing.module.css';
import { useNavigate } from 'react-router-dom';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  useGetFeedbackFormQuery,
  useAddFeedbackMutation,
} from '../../../../services/settings';

import SelectBox from '../../../../components/SelectBox/SelectBox';
import {
  CHARACTER_COUNT,
  CONTENT,
  ENTER,
  ENTER_CONTENT,
  ENTER_TITLE,
  FEEDBACK_TYPE,
  RECEIVE_EMAIL_REPLIES,
  SEND,
  TITLE,
  WRITING_FEEDBACK,
} from '../../../../constants';
import { limitMaxLen } from '../../../../utils/util';

import { feedbackPageChanged } from '../../../../features/settingsSlice';
import { useAppDispatch } from '../../../../hooks/useRedux';

export default function Writing() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const { data: formList, isLoading } = useGetFeedbackFormQuery();
  const [sendFeedback] = useAddFeedbackMutation();
  const navigate = useNavigate();
  const [isEnableSubmit, setIsEnableSubmit] = useState(false);

  const schema = yup.object({
    qnaCd: yup.string().required(),
    title: yup.string().required(),
    content: yup.string().required(),
    sendEmailYn: yup.boolean().notRequired(),
  });

  const {
    register,
    handleSubmit,
    formState: { touchedFields },
    setValue,
    control,
    watch,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      qnaCd: '',
      title: '',
      content: '',
      sendEmailYn: false,
    },
  });

  const setInputValue = (
    name: 'qnaCd' | 'title' | 'content' | 'sendEmailYn',
    value: string | boolean
  ) => {
    setValue(name, value);
    touchedFields.title && touchedFields.content && setIsEnableSubmit(true);
  };

  const onSubmit = async (data: any) => {
    data.sendEmailYn = +data.sendEmailYn;

    try {
      await sendFeedback(data).unwrap();
      dispatch(feedbackPageChanged(0));
      navigate(-1);
    } catch (error) {
      console.log('Failed to send feedback', error);
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      schema
        .isValid(value)
        .then((res) => {
          setIsEnableSubmit(res);
        })
        .catch((err) => console.log(`Check required values: ${err}`));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  if (isLoading || !formList) return null;

  return (
    <>
      <h2>{WRITING_FEEDBACK}</h2>
      <form>
        <div className={cx('board_form_area')}>
          <div className={cx('mw196')}>
            <span className={cx('blind')}>{FEEDBACK_TYPE}</span>
            <Controller
              render={({}) => (
                <SelectBox
                  list={formList}
                  name="qnaCd"
                  setValue={setInputValue}
                />
              )}
              name="qnaCd"
              control={control}
              rules={{}}
            ></Controller>
          </div>

          <div className={cx('input_box')}>
            <span className={cx('blind')}>{TITLE}</span>
            <input
              type="text"
              title={`${TITLE} ${ENTER}`}
              placeholder={ENTER_TITLE}
              autoCapitalize="off"
              {...register('title', {
                onChange: ({ target }) =>
                  (target.value = limitMaxLen(target.value, 25)),
              })}
            />
          </div>

          <div className={cx('input_box')}>
            <span className={cx('blind')}>{CONTENT}</span>
            <textarea
              title={`${CONTENT} ${ENTER}`}
              placeholder={ENTER_CONTENT}
              {...register('content', {
                onChange: ({ target }) =>
                  setValue('content', limitMaxLen(target.value, 1000)),
              })}
            ></textarea>
            <span className={cx('letter_length')}>
              <span>{watch('content').length}</span>/1,000 {CHARACTER_COUNT}
            </span>
          </div>

          <div className={cx('row')}>
            <label>
              <input
                type="checkbox"
                className={cx('blind')}
                {...register('sendEmailYn')}
              />
              <span className={cx('clabel')}>{RECEIVE_EMAIL_REPLIES}</span>
            </label>
          </div>
        </div>

        <div className={cx('btn_wrap', 'bottom', 'mx_min')}>
          <button
            type="button"
            className={cx('btn', 'b_t', 's48', {
              'is--disabled': !isEnableSubmit,
            })}
            onClick={handleSubmit(onSubmit)}
          >
            <span>{SEND}</span>
          </button>
        </div>
      </form>
    </>
  );
}
